import { useState, useEffect } from 'react';
import {
  FaSearch,
  FaFilter,
  FaEye,
  FaPrint,
  FaClipboardCheck,
  FaTimes,
  FaTruck,
  FaCheck,
} from 'react-icons/fa';
import LoadingSpinner from '../LoadingSpinner';
import '../../styles/admin/OrdersManagement.css';

const OrdersManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage] = useState(10);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/admin/orders', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch orders');
        }

        const data = await response.json();
        setOrders(data);
      } catch (error) {
        // Error will be handled by the global error handler
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const handleViewOrder = order => {
    setSelectedOrder(order);
    setShowOrderDetails(true);
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      const response = await fetch(`/api/admin/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        throw new Error('Failed to update order status');
      }

      // Update local state
      setOrders(
        orders.map(order => (order.id === orderId ? { ...order, status: newStatus } : order)),
      );

      // If viewing order details, update the selected order
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
    } catch (error) {
      // Error will be handled by the global error handler
    }
  };

  const handlePrintOrder = orderId => {
    window.open(`/api/admin/orders/${orderId}/print`, '_blank');
  };

  // Filter and search orders
  const filteredOrders = orders.filter(order => {
    const matchesSearch =
      order.order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'all' || order.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  // Pagination
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);

  const renderPagination = () => {
    if (totalPages <= 1) {
      return null;
    }

    return (
      <div className='pagination'>
        <button
          className='pagination-btn'
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(1)}
        >
          First
        </button>
        <button
          className='pagination-btn'
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
        >
          Previous
        </button>
        <span className='page-info'>
          Page {currentPage} of {totalPages}
        </span>
        <button
          className='pagination-btn'
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(currentPage + 1)}
        >
          Next
        </button>
        <button
          className='pagination-btn'
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(totalPages)}
        >
          Last
        </button>
      </div>
    );
  };

  // Status badge
  const getStatusBadge = status => {
    switch (status) {
      case 'pending':
        return <span className='status-badge pending'>Pending</span>;
      case 'processing':
        return <span className='status-badge processing'>Processing</span>;
      case 'shipped':
        return <span className='status-badge shipped'>Shipped</span>;
      case 'delivered':
        return <span className='status-badge delivered'>Delivered</span>;
      case 'cancelled':
        return <span className='status-badge cancelled'>Cancelled</span>;
      default:
        return <span className='status-badge'>{status}</span>;
    }
  };

  // Status action buttons
  const renderStatusActions = order => {
    const { id, status } = order;

    switch (status) {
      case 'pending':
        return (
          <>
            <button
              className='status-action process'
              onClick={() => handleUpdateStatus(id, 'processing')}
              title='Mark as Processing'
            >
              <FaClipboardCheck />
            </button>
            <button
              className='status-action cancel'
              onClick={() => handleUpdateStatus(id, 'cancelled')}
              title='Cancel Order'
            >
              <FaTimes />
            </button>
          </>
        );
      case 'processing':
        return (
          <button
            className='status-action ship'
            onClick={() => handleUpdateStatus(id, 'shipped')}
            title='Mark as Shipped'
          >
            <FaTruck />
          </button>
        );
      case 'shipped':
        return (
          <button
            className='status-action deliver'
            onClick={() => handleUpdateStatus(id, 'delivered')}
            title='Mark as Delivered'
          >
            <FaCheck />
          </button>
        );
      default:
        return null;
    }
  };

  return (
    <div className='orders-management'>
      {showOrderDetails && selectedOrder ? (
        <div className='order-details-modal'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h2>Order #{selectedOrder.order_number}</h2>
              <button className='close-btn' onClick={() => setShowOrderDetails(false)}>
                &times;
              </button>
            </div>
            <div className='modal-body'>
              <div className='order-info'>
                <div className='info-section'>
                  <h3>Customer Information</h3>
                  <p>
                    <strong>Name:</strong> {selectedOrder.customer_name}
                  </p>
                  <p>
                    <strong>Email:</strong> {selectedOrder.customer_email}
                  </p>
                  <p>
                    <strong>Phone:</strong> {selectedOrder.customer_phone}
                  </p>
                </div>

                <div className='info-section'>
                  <h3>Shipping Information</h3>
                  <p>
                    <strong>Address:</strong> {selectedOrder.shipping_address}
                  </p>
                  <p>
                    <strong>City:</strong> {selectedOrder.shipping_city}
                  </p>
                  <p>
                    <strong>State/Province:</strong> {selectedOrder.shipping_state}
                  </p>
                  <p>
                    <strong>Zip/Postal Code:</strong> {selectedOrder.shipping_zip}
                  </p>
                  <p>
                    <strong>Country:</strong> {selectedOrder.shipping_country}
                  </p>
                </div>

                <div className='info-section'>
                  <h3>Order Information</h3>
                  <p>
                    <strong>Order Date:</strong>{' '}
                    {new Date(selectedOrder.created_at).toLocaleString()}
                  </p>
                  <p>
                    <strong>Payment Method:</strong> {selectedOrder.payment_method}
                  </p>
                  <p>
                    <strong>Status:</strong> {getStatusBadge(selectedOrder.status)}
                  </p>
                </div>
              </div>

              <h3>Order Items</h3>
              <table className='items-table'>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Quantity</th>
                    <th>Price</th>
                    <th>Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedOrder.items.map(item => (
                    <tr key={item.id}>
                      <td>
                        <div className='product-info'>
                          <img src={item.image_url} alt={item.name} className='product-thumbnail' />
                          <div>
                            <p className='product-name'>{item.name}</p>
                            {item.size && <p className='product-variant'>Size: {item.size}</p>}
                            {item.color && <p className='product-variant'>Color: {item.color}</p>}
                          </div>
                        </div>
                      </td>
                      <td>{item.quantity}</td>
                      <td>${item.price.toFixed(2)}</td>
                      <td>${(item.price * item.quantity).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan='3' className='total-label'>
                      Subtotal
                    </td>
                    <td>${selectedOrder.subtotal.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td colSpan='3' className='total-label'>
                      Shipping
                    </td>
                    <td>${selectedOrder.shipping_fee.toFixed(2)}</td>
                  </tr>
                  {selectedOrder.discount > 0 && (
                    <tr>
                      <td colSpan='3' className='total-label'>
                        Discount
                      </td>
                      <td>-${selectedOrder.discount.toFixed(2)}</td>
                    </tr>
                  )}
                  <tr className='grand-total'>
                    <td colSpan='3' className='total-label'>
                      Total
                    </td>
                    <td>${selectedOrder.total.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>

              <div className='modal-actions'>
                <button className='btn-print' onClick={() => handlePrintOrder(selectedOrder.id)}>
                  <FaPrint /> Print Invoice
                </button>

                <div className='status-update'>
                  <label>Update Status:</label>
                  <div className='status-buttons'>
                    <button
                      className={`status-btn ${selectedOrder.status === 'pending' ? 'active' : ''}`}
                      onClick={() => handleUpdateStatus(selectedOrder.id, 'pending')}
                    >
                      Pending
                    </button>
                    <button
                      className={`status-btn ${selectedOrder.status === 'processing' ? 'active' : ''}`}
                      onClick={() => handleUpdateStatus(selectedOrder.id, 'processing')}
                    >
                      Processing
                    </button>
                    <button
                      className={`status-btn ${selectedOrder.status === 'shipped' ? 'active' : ''}`}
                      onClick={() => handleUpdateStatus(selectedOrder.id, 'shipped')}
                    >
                      Shipped
                    </button>
                    <button
                      className={`status-btn ${selectedOrder.status === 'delivered' ? 'active' : ''}`}
                      onClick={() => handleUpdateStatus(selectedOrder.id, 'delivered')}
                    >
                      Delivered
                    </button>
                    <button
                      className={`status-btn ${selectedOrder.status === 'cancelled' ? 'active' : ''}`}
                      onClick={() => handleUpdateStatus(selectedOrder.id, 'cancelled')}
                    >
                      Cancelled
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className='filters-bar'>
        <div className='search-box'>
          <FaSearch className='search-icon' />
          <input
            type='text'
            placeholder='Search orders by number, customer name or email...'
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>

        <div className='status-filter'>
          <FaFilter className='filter-icon' />
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value='all'>All Statuses</option>
            <option value='pending'>Pending</option>
            <option value='processing'>Processing</option>
            <option value='shipped'>Shipped</option>
            <option value='delivered'>Delivered</option>
            <option value='cancelled'>Cancelled</option>
          </select>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {filteredOrders.length === 0 ? (
            <div className='no-orders'>
              <p>No orders found matching your criteria.</p>
            </div>
          ) : (
            <>
              <div className='orders-table-container'>
                <table className='orders-table'>
                  <thead>
                    <tr>
                      <th>Order #</th>
                      <th>Date</th>
                      <th>Customer</th>
                      <th>Items</th>
                      <th>Total</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentOrders.map(order => (
                      <tr key={order.id}>
                        <td>{order.order_number}</td>
                        <td>{new Date(order.created_at).toLocaleDateString()}</td>
                        <td>
                          <div className='customer-info'>
                            <p>{order.customer_name}</p>
                            <p className='email'>{order.customer_email}</p>
                          </div>
                        </td>
                        <td>{order.items.length}</td>
                        <td>${order.total.toFixed(2)}</td>
                        <td>{getStatusBadge(order.status)}</td>
                        <td>
                          <div className='table-actions'>
                            <button
                              className='btn-view'
                              onClick={() => handleViewOrder(order)}
                              title='View Details'
                            >
                              <FaEye />
                            </button>

                            <button
                              className='btn-print'
                              onClick={() => handlePrintOrder(order.id)}
                              title='Print Invoice'
                            >
                              <FaPrint />
                            </button>

                            {renderStatusActions(order)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {renderPagination()}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default OrdersManagement;
